import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { AppSelect } from '../../../components/common/select';
import CurrencyInput from 'react-currency-input-field';
import { fetchStandardsByServiceId, fetchServiceItemsByStandardId, cloneRatesByService } from '../api/rateManagementApi';
import useModalEffect from '../../../hooks/useModalEffect';

const RateManagementForm = ({
  isOpen, onClose, onSubmit, serviceOptions, unitTypeOptions, workflowOptions, isEdit, initialData, onCloneSuccess
}) => {
  useModalEffect(isOpen);
  const [formData, setFormData] = useState({
    serviceId: '',
    standardId: [],
    productCategory: '',
    level: '',
    serviceItemId: [],
    unitTypeId: '',
    priceSettings: [
      {
        id: 1,
        rateFrom: '',
        rateTo: '',
        workFlowId: ''
      }
    ]
  });
  const [formErrors, setFormErrors] = useState({});
  const [standardOptions, setStandardOptions] = useState([]);
  const [serviceItemOptions, setServiceItemOptions] = useState([]);
  const [showCloneForm, setShowCloneForm] = useState(false);
  const [cloneData, setCloneData] = useState({
    sourceServiceId: '',
    targetServiceId: ''
  });
  const [isCloning, setIsCloning] = useState(false);

  // Fetch standards saat service berubah
  useEffect(() => {
    const fetchStandards = async () => {
      if (formData.serviceId) {
        try {
          const standards = await fetchStandardsByServiceId(formData.serviceId);
          setStandardOptions(standards);
        } catch (e) {
          setStandardOptions([]);
        }
      } else {
        setStandardOptions([]);
      }
    };
    fetchStandards();
  }, [formData.serviceId]);

  // Fetch service items saat standard berubah
  useEffect(() => {
    const fetchServiceItems = async () => {
      if (formData.standardId && formData.standardId.length > 0) {
        try {
          // Kirim semua standard ID yang terpilih
          const items = await fetchServiceItemsByStandardId(formData.standardId);
          setServiceItemOptions(items);
        } catch (e) {
          setServiceItemOptions([]);
        }
      } else {
        setServiceItemOptions([]);
      }
    };
    fetchServiceItems();
  }, [formData.standardId]);

  // Options for dropdowns
  // const workflowOptions = [
  //   { id: 'standardApproval', name: 'Standard Approval' },
  //   { id: 'expedited', name: 'Expedited Approval' },
  //   { id: 'special', name: 'Special Approval' }
  // ];

  const handleInputChange = (name, value) => {
    // Jika value adalah object (dari AppSelect), ambil id-nya
    if (value && typeof value === 'object' && value.id) {
      value = value.id;
    }
    // Untuk multiple select (array of object)
    if (Array.isArray(value) && value[0] && typeof value[0] === 'object' && value[0].id) {
      value = value.map(v => v.id);
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePriceSettingChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      priceSettings: prev.priceSettings.map((setting, i) =>
        i === index ? { ...setting, [field]: value } : setting
      )
    }));
  };

  const addPriceSetting = () => {
    setFormData(prev => ({
      ...prev,
      priceSettings: [
        ...prev.priceSettings,
        {
          id: prev.priceSettings.length + 1,
          rateFrom: '',
          rateTo: '',
          workFlowId: ''
        }
      ]
    }));
  };

  const deletePriceSetting = (index) => {
    if (formData.priceSettings.length === 1) return;
    setFormData(prev => ({
      ...prev,
      priceSettings: prev.priceSettings.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.serviceId) errors.serviceId = 'Service is required';
    if (!formData.standardId.length) errors.standardId = 'At least one standard is required';
    if (!formData.serviceItemId.length) errors.serviceItemId = 'At least one service item is required';
    if (!formData.unitTypeId) errors.unitTypeId = 'Unit type is required';

    const priceSettingsErrors = formData.priceSettings.map(setting => {
      const settingError = {};
      if (!setting.rateFrom) settingError.rateFrom = 'Rate from is required';
      if (!setting.rateTo) settingError.rateTo = 'Rate to is required';
      if (!setting.workFlowId) settingError.workFlowId = 'Workflow is required';
      return Object.keys(settingError).length > 0 ? settingError : null;
    });

    if (priceSettingsErrors.some(error => error !== null)) {
      errors.priceSettings = priceSettingsErrors;
    }

    return errors;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length === 0) {
      onSubmit(formData);
      // setFormData({
      //   serviceId: '',
      //   standardId: [],
      //   productCategory: '',
      //   level: '',
      //   serviceItemId: [],
      //   unitTypeId: '',
      //   priceSettings: [
      //     {
      //       id: 1,
      //       rateFrom: '',
      //       rateTo: '',
      //       workFlowId: ''
      //     }
      //   ]
      // });
    } else {
      setFormErrors(errors);
    }
  };

  const handleModalClose = () => {
    setFormData({
      serviceId: '',
      standardId: [],
      productCategory: '',
      level: '',
      serviceItemId: [],
      unitTypeId: '',
      priceSettings: [
        {
          id: 1,
          rateFrom: '',
          rateTo: '',
          workFlowId: ''
        }
      ]
    });
    setFormErrors({});
    setShowCloneForm(false);
    setCloneData({ sourceServiceId: '', targetServiceId: '' });
    onClose();
  };

  const handleCloneSubmit = async (e) => {
    e.preventDefault();
    if (!cloneData.sourceServiceId || !cloneData.targetServiceId) {
      alert('Please select both source and target services');
      return;
    }

    // Allow same source and target service
    // if (cloneData.sourceServiceId === cloneData.targetServiceId) {
    //   alert('Source and target services cannot be the same');
    //   return;
    // }

    setIsCloning(true);
    try {
      const result = await cloneRatesByService(cloneData.sourceServiceId, cloneData.targetServiceId);
      
      if (result.success) {
        // Use the message from backend which handles same service vs different service
        alert(result.message);
        if (onCloneSuccess) {
          onCloneSuccess();
        }
        setShowCloneForm(false);
        setCloneData({ sourceServiceId: '', targetServiceId: '' });
      } else {
        alert(`Clone failed: ${result.message}`);
      }
    } catch (error) {
      console.error('Error cloning rates:', error);
      alert('Failed to clone rates. Please try again.');
    } finally {
      setIsCloning(false);
    }
  };

  useEffect(() => {
    if (isOpen && isEdit && initialData) {
      setFormData({
        serviceId: initialData.serviceId || '',
        standardId: initialData.standardId || [],
        productCategory: initialData.productCategory || '',
        level: initialData.level || '',
        serviceItemId: initialData.serviceItemId || [],
        unitTypeId: initialData.unitTypeId || '',
        priceSettings: initialData.priceSettings
          ? initialData.priceSettings.map((ps, idx) => ({
              id: ps.id || idx + 1,
              rateFrom: ps.rateFrom || '',
              rateTo: ps.rateTo || '',
              workFlowId: ps.workFlowId || ''
            }))
          : [
              {
                id: 1,
                rateFrom: '',
                rateTo: '',
                workFlowId: ''
              }
            ]
      });
    } else if (isOpen && !isEdit) {
      // Reset ke default jika create
      setFormData({
        serviceId: '',
        standardId: [],
        productCategory: '',
        level: '',
        serviceItemId: [],
        unitTypeId: '',
        priceSettings: [
          {
            id: 1,
            rateFrom: '',
            rateTo: '',
            workFlowId: ''
          }
        ]
      });
    }
  }, [isOpen, isEdit, initialData]);

  // Modal animation effects
  ;

  // Cleanup effect when component unmounts
  useEffect(() => {
    return () => {
      // Clean up any remaining modal states when component unmounts
      const cleanupModal = () => {
        // Remove modal scroll lock
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.body.classList.remove('modal-backdrop');
      };
      
      cleanupModal();
    };
  }, []);


  // Cleanup effect: reset modal body state when component unmounts.
  useEffect(() => {
    return () => {
      // React owns the backdrop DOM (createPortal); only reset body state here.
      // Remove modal-open class from body
      document.body.classList.remove('modal-open');
    };
  }, []);

  return (
    <>
      <style>{`
        #modal-rate-management .inv-section-label {
          font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em;
          text-transform: uppercase; color: #6c7a7d; margin-bottom: 0.75rem;
          display: flex; align-items: center; gap: 0.4rem;
        }
        #modal-rate-management .inv-section-label::after {
          content: ''; flex: 1; height: 1px; background: #e9ecef;
        }
        #modal-rate-management .inv-footer {
          border-top: 1px solid #e9ecef !important;
          background: #fff;
          padding: 1rem 1.5rem;
          display: flex; align-items: center; justify-content: flex-end; gap: 0.6rem;
          flex-shrink: 0;
        }
        #modal-rate-management .btn-inv-save {
          display: inline-flex; align-items: center; gap: 0.4rem;
        }
        #modal-rate-management .btn-inv-save:disabled {
          opacity: 0.6; cursor: not-allowed;
        }
        #modal-rate-management .inv-required-star { color: #e53935; margin-left: 2px; }
      `}</style>
      {isOpen && ReactDOM.createPortal(<div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />, document.body)}
      <div
        className={`modal modal-blur fade${isOpen ? ' show' : ''}`} style={{ display: isOpen ? 'block' : 'none' }}
        id="modal-rate-management"
        tabIndex="-1"
        role="dialog"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {showCloneForm ? 'Clone Rates by Service' : (isEdit ? 'Edit Rate Management' : 'Add Rate Management')}
              </h5>
              <div className="btn-list">
                {!showCloneForm && (
                  <button 
                    type="button" 
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => setShowCloneForm(true)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-copy" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                      <path d="M8 8m0 2a2 2 0 0 1 2 -2h8a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-8a2 2 0 0 1 -2 -2z"></path>
                      <path d="M16 8v-2a2 2 0 0 0 -2 -2h-8a2 2 0 0 0 -2 2v8a2 2 0 0 0 2 2h2"></path>
                    </svg>
                    Clone Rates
                  </button>
                )}
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={onClose}
                  aria-label="Close"
                ></button>
              </div>
            </div>
            {showCloneForm ? (
              <form onSubmit={handleCloneSubmit}>
                <div className="modal-body">
                  <div className="alert alert-info">
                    <h4 className="alert-title">Clone Rates by Service</h4>
                    <div className="text-muted">
                      This will clone all rates from the source service to the target service. 
                      You can select the same service for both source and target to duplicate rates within the same service.
                      All price settings will have their workflow set to NULL initially.
                    </div>
                  </div>
                  
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <AppSelect
                        name="sourceServiceId"
                        value={cloneData.sourceServiceId}
                        onChange={(name, value) => setCloneData(prev => ({ ...prev, sourceServiceId: value }))}
                        options={serviceOptions}
                        placeholder="Select source service"
                        required
                        label="Source Service"
                        valueField="id"
                        labelField="name"
                        searchField="name"
                      />
                    </div>
                    <div className="col-md-6">
                      <AppSelect
                        name="targetServiceId"
                        value={cloneData.targetServiceId}
                        onChange={(name, value) => setCloneData(prev => ({ ...prev, targetServiceId: value }))}
                        options={serviceOptions}
                        placeholder="Select target service"
                        required
                        label="Target Service"
                        valueField="id"
                        labelField="name"
                        searchField="name"
                      />
                    </div>
                  </div>
                </div>
                <div className="inv-footer">
                  <span className="text-muted me-auto" style={{ fontSize: '0.75rem' }}>
                    {isCloning ? 'Cloning…' : (
                      <span style={{ color: '#9aa5b1' }}>
                        <span className="inv-required-star">*</span> Required fields
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowCloneForm(false)}
                    disabled={isCloning}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary btn-inv-save"
                    disabled={isCloning}
                  >
                    {isCloning ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: 14, height: 14, borderWidth: 2 }} />
                        Cloning…
                      </>
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                        Clone Rates
                      </>
                    )}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleFormSubmit}>
                <div className="modal-body">
                <div className="inv-section-label">
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                  Details
                </div>
                <div className="row mb-3">
                  <div className="col-md-12">
                    <AppSelect
                      name="serviceId"
                      value={formData.serviceId}
                      onChange={handleInputChange}
                      options={serviceOptions}
                      placeholder="Select service"
                      required
                      error={formErrors.serviceId}
                      label="Service"
                      valueField="id"
                      labelField="name"
                      searchField="name"
                    />
                  </div>
                  <div className="col-md-12">
                    <AppSelect
                      name="standardId"
                      value={formData.standardId}
                      onChange={handleInputChange}
                      options={standardOptions}
                      placeholder="Select standards"
                      required
                      multiple
                      error={formErrors.standardId}
                      label="Standards"
                      valueField="id"
                      labelField="name"
                      searchField="name"
                    />
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-12">
                    <AppSelect
                      name="serviceItemId"
                      value={formData.serviceItemId}
                      onChange={handleInputChange}
                      options={serviceItemOptions}
                      placeholder="Select service items"
                      required
                      multiple
                      error={formErrors.serviceItemId}
                      label="Service Items"
                    />
                  </div>
                  <div className="col-md-12">
                    <AppSelect
                      name="unitTypeId"
                      value={formData.unitTypeId}
                      onChange={handleInputChange}
                      options={unitTypeOptions}
                      placeholder="Select unit type"
                      required
                      error={formErrors.unitTypeId}
                      label="Unit Type"
                      valueField="id"
                      labelField="name"
                      searchField="name"
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Price List Settings</label>
                  {formData.priceSettings.map((setting, index) => (
                    <div key={setting.id} className="card mb-3">
                      <div className="card-body">
                        <div className="row g-3">
                          <div className="col-md-3">
                            <label className="form-label">Price Range</label>
                            <CurrencyInput
                              className={`form-control ${formErrors.priceSettings?.[index]?.rateFrom ? 'is-invalid' : ''}`}
                              value={setting.rateFrom}
                              onValueChange={(value) => handlePriceSettingChange(index, 'rateFrom', value)}
                              placeholder="From"
                              prefix="Rp "
                              decimalsLimit={2}
                              allowNegativeValue={false}
                            />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label">Rate</label>
                            <CurrencyInput
                              className={`form-control ${formErrors.priceSettings?.[index]?.rateTo ? 'is-invalid' : ''}`}
                              value={setting.rateTo}
                              onValueChange={(value) => handlePriceSettingChange(index, 'rateTo', value)}
                              placeholder="To"
                              prefix="Rp "
                              decimalsLimit={2}
                              allowNegativeValue={false}
                            />
                          </div>
                          <div className="col-md-5">
                            <AppSelect
                              name={`workFlowId-${index}`}
                              value={setting.workFlowId}
                              onChange={(_, value) => handlePriceSettingChange(index, 'workFlowId', value)}
                              options={workflowOptions}
                              placeholder="Select workflow"
                              required
                              error={formErrors.priceSettings?.[index]?.workFlowId}
                              label="Workflow"
                              valueField="id"
                              labelField="workflowName"
                              searchField="workflowName"
                            />
                          </div>
                          <div className="col-md-1">
                            <div className="d-flex h-100 justify-content-center align-items-center">
                              {formData.priceSettings.length > 1 && (
                                <button
                                  type="button"
                                  className="btn btn-icon btn-ghost-danger"
                                  onClick={() => deletePriceSetting(index)}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-trash" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                    <path d="M4 7l16 0"></path>
                                    <path d="M10 11l0 6"></path>
                                    <path d="M14 11l0 6"></path>
                                    <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12"></path>
                                    <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"></path>
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={addPriceSetting}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-plus" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                      <path d="M12 5l0 14"></path>
                      <path d="M5 12l14 0"></path>
                    </svg>
                    Add Price Setting
                  </button>
                </div>
              </div>
              <div className="inv-footer">
                <span className="text-muted me-auto" style={{ fontSize: '0.75rem' }}>
                  <span style={{ color: '#9aa5b1' }}>
                    <span className="inv-required-star">*</span> Required fields
                  </span>
                </span>
                <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary btn-inv-save">
                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  {isEdit ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

RateManagementForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  serviceOptions: PropTypes.array.isRequired,
  unitTypeOptions: PropTypes.array.isRequired,
  workflowOptions: PropTypes.array.isRequired,
  isEdit: PropTypes.bool,
  initialData: PropTypes.object,
  onCloneSuccess: PropTypes.func,
};

export default RateManagementForm; 